import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Users, 
  ArrowRight,
  Check,
  Globe,
  Building2,
  Plug,
  TrendingUp,
  Layers,
  Monitor,
  Handshake,
  DollarSign,
  Award,
  Headphones,
  BarChart3,
  Shield,
  Zap,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Partners() {
  useEffect(() => {
    document.title = "Connect | Partner Program";
    window.scrollTo(0, 0);
  }, []);

  const audiences = [
    {
      icon: Phone,
      title: 'PBX Resellers',
      description: 'Add a modern web console to your PBX offerings. Give your customers the tools they expect with your branding and support.'
    },
    {
      icon: Monitor,
      title: 'IT Consultants',
      description: 'Simplify phone system management for your clients. One dashboard to monitor and support all their communications needs.'
    },
    {
      icon: Layers,
      title: 'Managed Service Providers',
      description: 'Scale your voice services across multiple clients. Centralized management with per-client isolation and reporting.'
    }
  ];

  const partnerBenefits = [
    {
      icon: DollarSign,
      title: 'Recurring Revenue',
      description: 'Get wholesale pricing on every seat, every month. Build predictable recurring revenue.'
    },
    {
      icon: Award,
      title: 'NFR Subscription',
      description: 'Get NFR access for demos and internal use. Experience the platform before you sell it.'
    },
    {
      icon: Users,
      title: 'Free Tier for Trials',
      description: 'Let your customers try before they buy with our free tier. Lower the barrier to adoption.'
    },
    {
      icon: TrendingUp,
      title: 'Volume Discounts',
      description: 'The more seats you bring, the better your margins. Tiered pricing rewards growth.'
    },
    {
      icon: Headphones,
      title: 'Partner Support',
      description: 'Dedicated support channel for partners. Get answers fast so you can support your clients.'
    },
    {
      icon: Shield,
      title: 'Your Relationship',
      description: 'You own the customer relationship. We provide the platform, you provide the service.'
    }
  ];

  const integrations = [
    { name: 'Hero Internet', status: 'Available', description: 'NZ Cloud PBX' },
    { name: 'More Providers', status: 'Coming Soon', description: 'Additional integrations' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/connect-logo-dark.png" alt="CONNECT" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-bold text-white tracking-tight">CONNECT</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/features" className="text-slate-300 hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="text-slate-300 hover:text-white transition-colors">Pricing</Link>
            <Link to="/business" className="text-slate-300 hover:text-white transition-colors">For Business</Link>
            <Link to="/partners" className="text-amber-400 font-medium">Partners</Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/partner/login">
              <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
                Partner Login
              </Button>
            </Link>
            <Link to="/partner/login">
              <Button className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white shadow-lg shadow-amber-500/25">
                Become a Partner
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm mb-8">
            <Handshake className="w-4 h-4" />
            Reseller Program
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
            Grow Your Business
            <span className="block bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
              With Recurring Revenue
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto mb-10 leading-relaxed">
            Partner with CONNECT and offer your clients a modern Cloud PBX operator console. 
            Get wholesale pricing while we handle the platform.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/partner/login?tab=register">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white px-8 py-6 text-lg shadow-xl shadow-amber-500/25">
                Apply to Partner Program
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/partner/login">
              <Button size="lg" variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 px-8 py-6 text-lg">
                Existing Partner? Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Built for Voice Professionals */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Built for Voice Professionals
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Whether you're reselling PBX services, consulting on IT infrastructure, or managing communications for multiple clients — CONNECT gives you the tools to succeed.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {audiences.map((audience, i) => (
              <div 
                key={i}
                className="group p-8 rounded-2xl bg-gradient-to-b from-slate-800/50 to-slate-800/30 border border-slate-700/50 hover:border-amber-500/30 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-600/20 flex items-center justify-center mb-6 group-hover:from-amber-500/30 group-hover:to-orange-600/30 transition-all">
                  <audience.icon className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{audience.title}</h3>
                <p className="text-slate-400 leading-relaxed">{audience.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Benefits */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Partner Program Benefits
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Everything you need to successfully resell CONNECT to your clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partnerBenefits.map((benefit, i) => (
              <div 
                key={i}
                className="group p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50 hover:border-amber-500/30 hover:bg-slate-800/50 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-600/20 flex items-center justify-center mb-4 group-hover:from-amber-500/30 group-hover:to-orange-600/30 transition-all">
                  <benefit.icon className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Integrations */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm mb-6">
              <Plug className="w-4 h-4" />
              Platform Integrations
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Works With Your Existing PBX
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Integrate CONNECT with your existing Cloud PBX provider and offer your customers a modern web console.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-6 max-w-3xl mx-auto">
            {integrations.map((integration, i) => (
              <div 
                key={i}
                className={`flex-1 p-6 rounded-2xl border ${
                  integration.status === 'Available' 
                    ? 'bg-gradient-to-br from-slate-800/50 to-slate-900/50 border-slate-700/50' 
                    : 'bg-slate-800/20 border-slate-700/30 border-dashed'
                }`}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    integration.status === 'Available' 
                      ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20' 
                      : 'bg-slate-700/30'
                  }`}>
                    <Globe className={`w-6 h-6 ${integration.status === 'Available' ? 'text-orange-400' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <h3 className={`font-bold ${integration.status === 'Available' ? 'text-white' : 'text-slate-400'}`}>
                      {integration.name}
                    </h3>
                    <p className={`text-sm ${integration.status === 'Available' ? 'text-slate-400' : 'text-slate-500'}`}>
                      {integration.description}
                    </p>
                  </div>
                  <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${
                    integration.status === 'Available' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {integration.status}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            Have a specific provider in mind? <Link to="/partner/login" className="text-amber-400 hover:underline">Let us know</Link> and we'll prioritize it.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How the Partner Program Works
            </h2>
          </div>

          <div className="space-y-8">
            {[
              { step: '1', title: 'Apply & Get Approved', description: 'Complete the partner application. We\'ll review and get back to you within 24 hours.' },
              { step: '2', title: 'Get Your NFR Account', description: 'Receive your NFR subscription to demo the platform and use internally.' },
              { step: '3', title: 'Start Reselling', description: 'Get your unique referral link and start offering CONNECT to your clients.' },
              { step: '4', title: 'Bill Your Customers', description: 'Pay wholesale rates and set your own retail prices. Keep the margin on every active seat.' }
            ].map((item, i) => (
              <div key={i} className="flex gap-6 items-start">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-y border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Grow Your Business?
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Join our partner program and start earning recurring revenue today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/partner/login?tab=register">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white px-8 py-6 text-lg font-semibold">
                Apply Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <img src="/connect-logo-dark.png" alt="CONNECT" className="w-8 h-8 rounded-lg" />
            <span className="text-white font-bold">CONNECT</span>
          </Link>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <Link to="/features" className="hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/business" className="hover:text-white transition-colors">For Business</Link>
            <Link to="/partners" className="hover:text-white transition-colors">Partners</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
          <p className="text-sm text-slate-500">© {new Date().getFullYear()} Connect-US. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
